import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, take, tap } from 'rxjs';
import { User } from '../user/user.types';
import { ActivatedRoute, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { OnboardingService } from 'app/modules/shared/services/onboarding.service';
import { SECURITY_NOTICE_KEY } from 'app/modules/shared/services/security-notice.service';
import { returnPageOf } from 'app/modules/shared/utils/third-party-data';
import { peekReturnPage, RETURN_PAGE_KEY } from 'app/core/auth/auth.config';
import * as Sentry from '@sentry/angular';

export interface IUserLogin {
    // email: string;
    username: string;
    password: string;
    rememberMe?: boolean;
}

// localStorage flag: the user of this session has been loaded. Replaces the former 'user' entry, which held
// the name, e-mail and ids of the user and is removed from browsers that still have it.
const USER_LOADED_KEY = 'userLoaded';
const LEGACY_USER_KEY = 'user';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _userService = inject(UserService);

    private _authenticated: boolean = false;
    private _accessToken: string | null = null;
    private loggedUser!: User | null;
    // private loggedUser!: string | null;

    // BSI TR-03161-2 O.Auth_8 — how long the app can sit backgrounded
    // (tab hidden / window minimized) before we require a fresh login
    // challenge on resume, rather than silently continuing the existing
    // session. A brief tab switch shouldn't interrupt the user.
    private readonly BACKGROUND_REAUTH_THRESHOLD_MS = 30 * 60 * 1000;
    private _hiddenAt: number | null = null;
    private _backgroundReauthRegistered = false;

    // BSI TR-03161-2 O.Auth_9 — how long the session may sit with no
    // keyboard/mouse/touch/scroll activity (tab still focused) before
    // we auto-logout. Independent of O.Auth_8 above, which only covers
    // the tab-backgrounded case — this covers an unattended-but-visible
    // session (e.g. a shared kiosk/waiting-room device left signed in).
    private readonly IDLE_TIMEOUT_MS = 30 * 60 * 1000;
    private readonly IDLE_RESET_THROTTLE_MS = 1000;
    private _idleTimer: ReturnType<typeof setTimeout> | null = null;
    private _idleLogoutRegistered = false;
    private _lastIdleResetAt = 0;

    // BSI TR-03161-2 O.Auth_10 — the maximum total duration a single
    // login may stay active before we require a real (non-silent)
    // re-authentication, even if the user has been continuously active
    // the whole time. Without this, silentRenew/forceRefreshSession can
    // extend a session indefinitely via the IdP's own SSO cookie — this
    // is what actually "reactivates the server session" rather than
    // rubber-stamping the existing one forever. Persisted to localStorage
    // (not just an in-memory field) so a page reload doesn't reset the
    // clock on an already-running session.
    private readonly MAX_ACTIVE_SESSION_MS = 12 * 60 * 60 * 1000;

    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            // 'X-Requested-With': 'XMLHttpRequest',
            // 'Access-Control-Allow-Origin': '*'
        }),
    };

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get authenticated(): boolean {
        return this._authenticated;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ///Fetch User Data
    private fetchUserData(): Observable<any> {
        return this._userService.getUserBase().pipe(
            switchMap((_user: any) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                const user: User = _user;
                user.active = true;
                this._userService.user = user;
                this._userService.activeUser = user;
                this.loggedUser = user;

                // Only a flag: no personal data (name, e-mail, ids) is written to the browser's storage
                // (BSI TR-03161-2 O.Arch_4, O.Data_14). The user lives in memory (UserService).
                this.setStore(USER_LOADED_KEY, 'true');

                // this.sendFCMToken(user.userId);

                // Return true
                return of(true);
            }),
            catchError(() => {
                // Return false
                return of(false);
            }),
        );
    }

    ///Send FCM Token
    private sendFCMToken(userId: number) {
        let fcm = '';
        // show loading
        this._userService.sendFCMToken(userId, fcm).subscribe((success) => {
            // hide loading
            if (success) {
                // navigate to home
            }
        });
    }

    /**
     * Sign out
     */
    signOut(returnToThisPage = false): Observable<any> {
        // A sign-out that the session forces (expired session, idle timeout) brings the user back to the page after the
        // next sign-in; a sign-out the user asks for does not.
        if (returnToThisPage) {
            this.rememberCurrentPage();
        } else {
            this.forgetReturnPage();
        }
        this.removeStore(USER_LOADED_KEY);
        // Set the authenticated flag to false
        this._authenticated = false;
        this.loggedUser = null;
        this._accessToken = null;
        this.clearSentry();
        this.logout();
        // The "security notice confirmed" flag is not user data: keep it, so the notice does not reappear at every sign-in
        const securityNoticeSeen = localStorage.getItem(SECURITY_NOTICE_KEY);
        localStorage.clear();
        if (securityNoticeSeen) {
            localStorage.setItem(SECURITY_NOTICE_KEY, securityNoticeSeen);
        }
        // Return the observable
        return of(true);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        let user = this.getStore(USER_LOADED_KEY);
        if (this.authenticated) {
            return this.loggedUser
                ? of(true)
                : user
                  ? of(true)
                  : this.fetchUserData();
        }
        return of(false);
    }

    // OAUTH openID

    constructor(
        private oidcSecurityService: OidcSecurityService,
        private router: Router,
        private _activateRoute: ActivatedRoute,
        private _onBoard: OnboardingService,
    ) {
        // Earlier versions kept the name, e-mail and ids of the user in localStorage.
        this.removeStore(LEGACY_USER_KEY);

        this._userService.user$.subscribe((user) => {
            if (user) {
                this.loggedUser = user;
                if (user.userId) {
                    this.setUserIdInSentry(user.userId);
                }
            }
        });
    }

    configureSingleSignOn() {
        this.oidcSecurityService.checkAuth().subscribe(
            ({ isAuthenticated, accessToken }) => {
                if (!isAuthenticated) {
                    this.removeStore(USER_LOADED_KEY);
                    this._authenticated = false;
                    this._accessToken = null;
                    this.loggedUser = null;
                    // Create loginCount to handle login loop oidc.
                    let count = JSON.parse(this.getStore('loginCount')) | 0;
                    if (count > 1) {
                        this.removeStore('loginCount');
                        this.logout();
                    } else {
                        this.setStore('loginCount', JSON.stringify(count + 1));
                        this.login();
                    }
                } else {
                    this._authenticated = isAuthenticated;
                    this._accessToken = accessToken;
                    // Only stamp this on the first checkAuth() of a given
                    // login — a page reload re-runs configureSingleSignOn()
                    // against the same still-valid session and must NOT
                    // push the clock back out, or O.Auth_10's cap would
                    // never actually trigger for a long-lived browser tab.
                    if (!this.getStore('sessionStartedAt')) {
                        this.setStore('sessionStartedAt', Date.now().toString());
                    }
                    this.onValidToken();
                    this.removeStore('loginCount');
                    this.initOnBoarding();
                }
            },
            (error) => {
                this.signOut();
            },
        );
    }

    /**
     * BSI TR-03161-2 O.Auth_8 — starts watching document.visibilitychange
     * so that returning from an extended backgrounding forces a fresh
     * IdP login challenge instead of silently resuming the old session
     * (e.g. someone else could have gained physical access to the
     * device while it was backgrounded). Safe to call multiple times;
     * only registers the listener once.
     */
    registerBackgroundingReauth(): void {
        if (this._backgroundReauthRegistered || typeof document === 'undefined') {
            return;
        }
        this._backgroundReauthRegistered = true;

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this._hiddenAt = Date.now();
                return;
            }

            if (this._hiddenAt === null) {
                return;
            }
            const elapsedMs = Date.now() - this._hiddenAt;
            this._hiddenAt = null;

            if (elapsedMs >= this.BACKGROUND_REAUTH_THRESHOLD_MS && this._authenticated) {
                this.forceReAuthentication();
            }
        });
    }

    /**
     * Forces a fresh IdP-level login challenge (prompt=login) rather
     * than a silent checkAuth()/token-refresh — the point of O.Auth_8
     * (backgrounding) and O.Auth_10 (max active-session duration) is to
     * re-prove identity, not just confirm the existing token is still
     * technically valid. Public since both the visibilitychange handler
     * below and the HTTP interceptor's 401/403 handling (O.Auth_10) call
     * this directly.
     */
    forceReAuthentication(): void {
        this.rememberCurrentPage();
        this.removeStore(USER_LOADED_KEY);
        this.removeStore('sessionStartedAt');
        this._authenticated = false;
        this._accessToken = null;
        this.loggedUser = null;
        this.oidcSecurityService.authorize(undefined, {
            customParams: { prompt: 'login' },
        });
    }

    /**
     * BSI TR-03161-2 O.Auth_10 — true once the current login has been
     * active for longer than MAX_ACTIVE_SESSION_MS, regardless of how
     * recently the access token itself was issued or silently renewed.
     * The HTTP interceptor checks this before attempting a silent
     * refresh on a 401/403, so a session can't be extended forever just
     * by staying active.
     */
    hasExceededMaxActiveSession(): boolean {
        const startedAt = this.getStore('sessionStartedAt');
        if (!startedAt) {
            return false;
        }
        return Date.now() - parseInt(startedAt, 10) >= this.MAX_ACTIVE_SESSION_MS;
    }

    /**
     * BSI TR-03161-2 O.Auth_9 — starts an inactivity timer that
     * auto-logs-out the user after IDLE_TIMEOUT_MS with no keyboard/
     * mouse/touch/scroll activity, regardless of tab visibility.
     * Listeners are throttled so a flood of mousemove/scroll events
     * doesn't churn clearTimeout/setTimeout on every frame. Safe to
     * call multiple times; only registers listeners once.
     */
    registerIdleAutoLogout(): void {
        if (this._idleLogoutRegistered || typeof document === 'undefined') {
            return;
        }
        this._idleLogoutRegistered = true;

        const resetIdleTimer = (): void => {
            const now = Date.now();
            if (now - this._lastIdleResetAt < this.IDLE_RESET_THROTTLE_MS) {
                return;
            }
            this._lastIdleResetAt = now;

            if (this._idleTimer !== null) {
                clearTimeout(this._idleTimer);
            }
            // Auth state at fire-time (not at registration/reset-time)
            // decides whether to actually sign out, so this works
            // correctly regardless of whether login has completed yet.
            this._idleTimer = setTimeout(() => {
                if (this._authenticated) {
                    this.signOut(true);
                }
            }, this.IDLE_TIMEOUT_MS);
        };

        ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'].forEach(
            (eventName) => {
                document.addEventListener(eventName, resetIdleTimer, {
                    passive: true,
                });
            },
        );

        resetIdleTimer();
    }

    onValidToken() {
        if (!this.loggedUser) {
            this.fetchUserData()
                .pipe(take(1))
                .subscribe(() => {
                    this.navigateToHome();
                });
        } else {
            this.navigateToHome();
        }
    }

    navigateToHome() {
        // After a sign-in the user goes back to the page they were on; without one, to the start page
        // (the page stays in storage until the navigation has ended, so that the guards agree on it meanwhile)
        this.router
            .navigateByUrl(peekReturnPage() ?? '/configuration-user/personal')
            .then(() => this.forgetReturnPage());
    }

    /** Keeps the page the user is on for the time of the sign-in. Keeps an earlier one when this is no page. */
    private rememberCurrentPage(): void {
        const page = returnPageOf(window.location);
        if (!page) {
            return;
        }
        try {
            sessionStorage.setItem(RETURN_PAGE_KEY, page);
        } catch {
            // storage unavailable: the user lands on the start page
        }
    }

    private forgetReturnPage(): void {
        try {
            sessionStorage.removeItem(RETURN_PAGE_KEY);
        } catch {
            // storage unavailable
        }
    }


    initOnBoarding() {
        setTimeout(() => {
            this._onBoard
                .getOnboarding()
                .pipe(take(1))
                .subscribe((onboard: any) => {
                    if (onboard && !onboard.commitments) {
                        this._onBoard
                            .getOrder()
                            .pipe(
                                take(1),
                                // tap((res: any) => {
                                //     res.orderFlow.insurance = false;
                                //     res.products = res.products.filter(
                                //         (p) => !p.sku.includes('MN'),
                                //     );
                                //     return res;
                                // }),
                            )
                            .subscribe((res: any) => {
                                if (res?.orderId) {
                                    this.router.navigate([
                                        'onboarding/caretaker',
                                    ]);
                                }
                            });
                    }
                });
        }, 1000);
    }

    private navigateToLoginPage() {
        // TODO: Remember current URL
        this.router.navigateByUrl('/sign-in');
    }

    public get accessToken() {
        return this._accessToken;
    }

    login(): void {
        this.rememberCurrentPage();
        this.oidcSecurityService.authorize();
    }

    refreshSession(): void {
        this.oidcSecurityService.forceRefreshSession().subscribe();
    }

    logout(): void {
        this.oidcSecurityService.logoff().subscribe();
    }

    // logoffAndRevokeTokens(): void {
    //     this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
    // }

    revokeRefreshToken(): void {
        this.oidcSecurityService.revokeRefreshToken().subscribe();
    }

    revokeAccessToken(): void {
        this.oidcSecurityService.revokeAccessToken().subscribe();
    }

    forceRefreshSession() {
        // return this.oidcSecurityService.getRefreshToken();
        return this.oidcSecurityService.forceRefreshSession().pipe(
            // the interceptor sends this token with every request from now on, not only with the repeated one
            tap((result) => {
                if (result?.accessToken) {
                    this._accessToken = result.accessToken;
                }
            }),
        );
    }

    setStore(key: string, data: string) {
        localStorage.setItem(key, data);
    }

    getStore(key: string): string {
        return localStorage.getItem(key);
    }

    removeStore(key: string) {
        return localStorage.removeItem(key);
    }

    // Call this method after a successful login
    setUserIdInSentry(id: any): void {
        Sentry.setUser({
            id,
        });
    }

    clearSentry() {
        // Clear user details so upcoming errors aren't incorrectly attributed
        Sentry.setUser(null);
    }
}
